"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { CTAButton } from "@/shadcn/ui/button";
import type { GROQButton } from "@/sanity/lib/definitions";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/shadcn/ui/carousel";
import {
  Asset,
  ChallengeCol,
  CustomSpec,
  discounted as defaultDiscounted,
  FundedCol,
  fundedHeadlinePrice,
  Product,
  SizeId,
  VariantId,
  VisibleSize,
} from "@/lib/new-pricing/products";
import type { KpiKey, NewPricingCopy } from "./copy";
import InfoTip from "./InfoTip";
import frescoRenaissance from "@/public/new-pricing/fresco-renaissance.webp";
import frescoBaroque from "@/public/new-pricing/fresco-baroque.webp";

export type Stage = "challenge" | "funded";

export const fmtUsd = (n: number) => `$${n.toLocaleString("en-US")}`;

const SIZE_LABEL: Record<SizeId, string> = { "50k": "50K", "100k": "100K", "200k": "200K" };
const ROW_H = "min-h-[5.375rem]";

/** A rendered sub-column: variant + presel + row-value accessor + price. */
type SubCol = {
  variant: VariantId;
  presel: string;
  priceUsd: number | undefined;
  customSpecs?: CustomSpec[];
  value: (key: KpiKey) => string;
};

function challengeSubCol(col: ChallengeCol, copy: NewPricingCopy, maxAccounts: number | undefined): SubCol {
  const d = col.data;
  // literal doc semantics: a blank number renders "—"
  const usd = (n: number | undefined) => (n == null ? copy.values.dash : fmtUsd(n));
  return {
    variant: col.variant,
    presel: col.presel,
    priceUsd: d.priceUsd,
    customSpecs: col.customSpecs,
    value: (key) => {
      switch (key) {
        case "profitTarget":
          return usd(d.profitTargetUsd);
        case "maxDrawdown":
          return usd(d.maxDrawdownUsd);
        case "drawdownMode":
          return copy.values.drawdownMode;
        case "maxPositionLoss":
          return d.maxPositionLossPct != null ? `${d.maxPositionLossPct}% *` : copy.values.dash;
        case "dailyLossLimit":
          return d.dailyLossLimitUsd != null ? `${fmtUsd(d.dailyLossLimitUsd)} *` : copy.values.dash;
        case "consistency":
          return d.consistencyPct == null ? copy.values.none : `${d.consistencyPct}%`;
        case "duration":
          return d.durationDays == null ? copy.values.dash : copy.values.days(d.durationDays);
        case "extensionFee":
          return usd(d.extensionFeeUsd);
        case "maxAccounts":
          return maxAccounts == null ? copy.values.dash : String(maxAccounts);
        default:
          return copy.values.dash;
      }
    },
  };
}

function fundedSubCol(
  sizeEntry: VisibleSize,
  col: FundedCol,
  copy: NewPricingCopy,
  maxAccounts: number | undefined,
): SubCol {
  const d = col.data;
  const usd = (n: number | undefined) => (n == null ? copy.values.dash : fmtUsd(n));
  return {
    variant: col.variant,
    presel: col.presel,
    priceUsd: fundedHeadlinePrice(sizeEntry, col),
    customSpecs: col.customSpecs,
    value: (key) => {
      switch (key) {
        case "profitSplit":
          return d.profitSplitTrader == null
            ? copy.values.dash
            : `${d.profitSplitTrader} / ${100 - d.profitSplitTrader}`;
        case "payoutEvery":
          return d.payoutEveryDays == null ? copy.values.dash : copy.values.payoutDays(d.payoutEveryDays);
        case "consistency":
          return d.consistencyPct == null ? copy.values.none : `${d.consistencyPct}%`;
        case "maxDrawdown":
          return usd(d.maxDrawdownUsd);
        case "drawdownMode":
          return copy.values.drawdownMode;
        case "maxPositionLoss":
          return d.maxPositionLossPct != null ? `${d.maxPositionLossPct}% *` : copy.values.dash;
        case "dailyLossLimit":
          return d.dailyLossLimitUsd != null ? `${fmtUsd(d.dailyLossLimitUsd)} *` : copy.values.dash;
        case "activationFee":
          if (d.activationFeeUsd == null) return copy.values.dash;
          return d.activationFeeUsd === "free" ? copy.values.free : fmtUsd(d.activationFeeUsd);
        case "resetFee":
          return d.resetFeeUsd == null ? copy.values.dash : fmtUsd(d.resetFeeUsd);
        case "extensionFee":
          return usd(d.extensionFeeUsd);
        case "maxAccounts":
          return maxAccounts == null ? copy.values.dash : String(maxAccounts);
        default:
          return copy.values.dash;
      }
    },
  };
}

type Row = { key: string; label: string; tooltipKeys: KpiKey[]; customId?: string };

const simpleRow = (key: KpiKey, copy: NewPricingCopy): Row => ({
  key,
  label: copy.kpiLabels[key],
  tooltipKeys: [key],
});

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.5 8.5H13.5M13.5 8.5L9 4M13.5 8.5L9 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Price bits ── */

function PriceLine({
  priceUsd,
  copy,
  compact,
  onLight,
  showSuffix = true,
  discounted = defaultDiscounted,
}: {
  priceUsd: number | undefined;
  copy: NewPricingCopy;
  compact?: boolean;
  onLight?: boolean;
  /** false = no "one-time fee" label (two-price split headers). */
  showSuffix?: boolean;
  discounted?: (priceUsd: number) => number;
}) {
  if (priceUsd == null) return null;
  return (
    <div className="flex flex-wrap items-end gap-[0.75rem]">
      <span
        className={cn(
          "font-geist leading-[1.1] line-through",
          onLight ? "text-black/40" : "text-white/60",
          compact ? "text-[2rem] " : "text-[2.5rem]",
        )}>
        {fmtUsd(priceUsd)}
      </span>
      <div
        className={cn(
          "font-geist leading-[1.05] font-semibold tracking-[-0.05em]  ",
          onLight ? "text-[#0d2922]" : "text-[#87f8e7]",
          compact ? "text-[3rem] leading-none " : "text-[3.25rem] !h-[4.5rem]",
        )}>
        {fmtUsd(discounted(priceUsd))}
      </div>
      {showSuffix && (
        <span
          className={cn(
            "font-geist leading-[1.3]",
            onLight ? "text-black/40" : "text-white/40",
            compact ? "text-[1.5rem]" : "text-[2rem]",
          )}>
          {copy.price.oneTimeFee}
        </span>
      )}
    </div>
  );
}

/**
 * Header price area. When the visible variants carry different prices
 * (Futures Core 50K: MPL $179 vs DLL $245 — the Figma card assumes one price)
 * one price renders per variant, aligned to its sub-column — SAME size as
 * single-price headers, without the "one-time fee" label (client feedback).
 */
function HeaderPrices({
  subCols,
  copy,
  discounted,
}: {
  subCols: SubCol[];
  copy: NewPricingCopy;
  discounted?: (priceUsd: number) => number;
}) {
  const prices = subCols.map((s) => s.priceUsd);
  const allEqual = prices.every((p) => p === prices[0]);
  if (allEqual || subCols.length < 2) {
    return (
      <PriceLine
        priceUsd={prices[0]}
        copy={copy}
        discounted={discounted}
      />
    );
  }
  return (
    <div className="flex gap-[1rem]">
      {subCols.map((sub) => (
        <div
          key={sub.presel + sub.variant}
          className="min-w-0 flex-1">
          <PriceLine
            priceUsd={sub.priceUsd}
            copy={copy}
            showSuffix={false}
            discounted={discounted}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Mobile card — one per account size. When a size has two variants the outline
 * button FLIPS the card in place to the other variant (client feedback: no
 * separate cards per variant). Header: size on top, the variant's full name on
 * its own single line below.
 */
function MobileSizeCard({
  entry,
  subCols,
  rows,
  copy,
  discounted,
  highlighted,
  stageToggle,
  labelCell,
  cellValue,
  ctaFor,
  variantLabel,
}: {
  entry: VisibleSize;
  subCols: SubCol[];
  rows: Row[];
  copy: NewPricingCopy;
  discounted: (priceUsd: number) => number;
  highlighted: boolean;
  stageToggle: React.ReactNode;
  labelCell: (row: Row) => React.ReactNode;
  cellValue: (sub: SubCol, row: Row) => string;
  ctaFor: (sub: SubCol, size: SizeId) => React.ReactNode;
  variantLabel: (v: VariantId) => string;
}) {
  const [variantIdx, setVariantIdx] = useState(0);
  // the stage toggle swaps the column set — clamp instead of resetting
  const idx = Math.min(variantIdx, Math.max(subCols.length - 1, 0));
  const sub = subCols[idx];
  if (!sub) return null;
  const other = subCols.length > 1 ? subCols[(idx + 1) % subCols.length] : null;
  const vLabel = variantLabel(sub.variant);

  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-[0.75rem] bg-white p-[1rem] [perspective:1200px]",
        highlighted && "border-2 border-[#307f6a]",
      )}>
      <AnimatePresence
        mode="wait"
        initial={false}>
        <motion.div
          // keyed by the variant INDEX, not the variant identity: only the
          // flip button changes it — stage/product/platform switches swap
          // content in place without re-triggering the flip animation
          key={idx}
          initial={{ rotateY: 90, opacity: 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          exit={{ rotateY: -90, opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeInOut" }}
          className="flex flex-1 flex-col gap-[2rem]">
          <div className="flex flex-col gap-[1rem]">
            <DarkHeader
              size={entry.size}
              copy={copy}
              highlighted={highlighted}
              variantLine={vLabel || undefined}>
              <PriceLine
                priceUsd={sub.priceUsd}
                copy={copy}
                discounted={discounted}
              />
            </DarkHeader>
            {stageToggle}
          </div>

          <div className="flex flex-col px-[0.5rem]">
            {rows.map((row) => (
              <div
                key={row.key}
                className="flex min-h-[6rem] items-center justify-between gap-[2rem] border-b border-dashed border-black/10 last:border-b-0">
                {labelCell(row)}
                <span className="text-right font-geist text-[2.125rem] leading-[1.25] font-medium text-black/80">
                  {cellValue(sub, row)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-auto flex flex-col gap-[1rem] pt-[1rem]">
            {other && (
              <button
                type="button"
                onClick={() => setVariantIdx((idx + 1) % subCols.length)}
                className="h-[6rem] w-full rounded-[0.5rem] border border-black/20 font-geist text-[2rem] leading-[1.3] font-semibold text-black transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]">
                {copy.cta.viewVariant(variantLabel(other.variant))}
              </button>
            )}
            {ctaFor(sub, entry.size)}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function DarkHeader({
  size,
  copy,
  highlighted,
  subtitle,
  variantLine,
  children,
}: {
  size: SizeId;
  copy: NewPricingCopy;
  highlighted: boolean;
  subtitle?: string;
  /** Full variant name on its own single line below "50K Account" (mobile). */
  variantLine?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        // fixed height (not min-h): the label column mirrors this exact value,
        // so label rows and card rows align deterministically in both modes.
        "relative flex h-[17.75rem] flex-col justify-between overflow-hidden rounded-[0.5rem] p-[3rem]",
        highlighted ? "bg-[#18473b]" : "bg-[#0d2922]",
      )}>
      <div
        className="pointer-events-none absolute inset-0 opacity-30 mix-blend-luminosity"
        aria-hidden="true">
        <Image
          src={highlighted ? frescoBaroque : frescoRenaissance}
          alt=""
          fill
          sizes="30rem"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(13,41,34,0.9)_20%,rgba(13,41,34,0)_70%)]" />
      </div>
      <div className="relative flex items-end gap-[0.5rem] text-white">
        <span className="font-geist text-[4rem] leading-[1.1] font-semibold tracking-[-0.05em]">
          {SIZE_LABEL[size]}
        </span>
        <span className="font-geist text-[2rem] leading-[1.4] opacity-60">{subtitle ?? copy.price.account}</span>
      </div>
      {variantLine && (
        <span className="relative font-geist text-[2rem] leading-[1.3] font-medium whitespace-nowrap text-white/90">
          {variantLine}
        </span>
      )}
      {children && <div className="relative">{children}</div>}
    </div>
  );
}

/* ── The table ─────────────────────────────────────────────────────────── */

export default function PlanTable({
  asset,
  product,
  sizes,
  stage,
  onStageChange,
  copy,
  makeCta,
  onCheckout,
  onSizeChange,
  discounted = defaultDiscounted,
  mostPopularSize = "50k",
}: {
  asset: Asset;
  product: Product;
  sizes: VisibleSize[];
  stage: Stage;
  onStageChange: (s: Stage) => void;
  copy: NewPricingCopy;
  makeCta: (presel: string) => GROQButton | null;
  onCheckout: (presel: string, variant: VariantId, size: SizeId) => void;
  onSizeChange?: (size: SizeId) => void;
  discounted?: (priceUsd: number) => number;
  mostPopularSize?: SizeId;
}) {
  void asset;
  const isInstant = product.id === "instant";
  const effectiveStage: Stage = isInstant ? "funded" : stage;

  // Per size: the sub-columns for the current stage.
  const perSize = sizes.map((s) => ({
    entry: s,
    subCols:
      effectiveStage === "challenge"
        ? s.challenge.map((col) => challengeSubCol(col, copy, product.maxAccounts))
        : s.funded.map((col) => fundedSubCol(s, col, copy, product.maxAccounts)),
  }));

  const anySplit = perSize.some(({ subCols }) => subCols.length > 1);
  // Detect the loss rules from the data itself: CFD columns are "single"
  // variants but still carry Max Position Loss.
  const hasMplData = perSize.some(({ subCols }) =>
    subCols.some((sc) => sc.value("maxPositionLoss") !== copy.values.dash),
  );
  const hasDllData = perSize.some(({ subCols }) =>
    subCols.some((sc) => sc.value("dailyLossLimit") !== copy.values.dash),
  );

  // A data-driven row renders only when at least one visible column carries a
  // value — hides Activation/Extension where removed, and shows Max Position
  // Loss / Daily Loss Limit as SEPARATE rows (never merged — client feedback).
  const hasData = (key: KpiKey) =>
    perSize.some(({ subCols }) => subCols.some((sc) => sc.value(key) !== copy.values.dash));

  const lossRows: Row[] = [
    ...(hasMplData ? [simpleRow("maxPositionLoss", copy)] : []),
    ...(hasDllData ? [simpleRow("dailyLossLimit", copy)] : []),
  ];

  const rows: Row[] =
    effectiveStage === "challenge"
      ? [
          simpleRow("profitTarget", copy),
          simpleRow("maxDrawdown", copy),
          simpleRow("drawdownMode", copy),
          ...lossRows,
          simpleRow("consistency", copy),
          simpleRow("duration", copy),
          simpleRow("extensionFee", copy),
          simpleRow("maxAccounts", copy),
        ]
      : [
          simpleRow("profitSplit", copy),
          simpleRow("payoutEvery", copy),
          simpleRow("consistency", copy),
          simpleRow("maxDrawdown", copy),
          simpleRow("drawdownMode", copy),
          ...lossRows,
          ...(hasData("activationFee") ? [simpleRow("activationFee", copy)] : []),
          simpleRow("resetFee", copy),
          ...(hasData("extensionFee") ? [simpleRow("extensionFee", copy)] : []),
          simpleRow("maxAccounts", copy),
        ];

  // Marketing-added extra rows (Sanity customSpecs) — appended after the
  // standard rows; paired across columns by KPI key or label.
  const KPI_KEYS: KpiKey[] = Object.keys(copy.kpiLabels) as KpiKey[];
  const customRows: Row[] = [];
  {
    const seen = new Set<string>();
    for (const { subCols } of perSize)
      for (const sc of subCols)
        for (const cs of sc.customSpecs ?? []) {
          if (seen.has(cs.id)) continue;
          seen.add(cs.id);
          customRows.push({
            key: `custom:${cs.id}`,
            label: cs.label,
            tooltipKeys: cs.kpiKey && KPI_KEYS.includes(cs.kpiKey as KpiKey) ? [cs.kpiKey as KpiKey] : [],
            customId: cs.id,
          });
        }
  }
  rows.push(...customRows);

  const cellValue = (sub: SubCol, row: Row): string =>
    row.customId
      ? (sub.customSpecs?.find((cs) => cs.id === row.customId)?.value ?? copy.values.dash)
      : sub.value(row.key as KpiKey);

  // The `*` breach rules are not printed as footnotes — they live in the ⓘ
  // tooltip on the Max Position Loss / Daily Loss Limit row (copy.kpiTooltips).

  const variantLabel = (v: VariantId) => (v === "single" ? "" : copy.variantLabels[v as Exclude<VariantId, "single">]);

  // Most Popular = 50K by default (client decision overriding spec §2.5's
  // 100K); editable via the pricingSectionV3 doc.
  const highlightedSize: SizeId = mostPopularSize;

  /* ── Mobile carousel: ONE card per size — the variant button FLIPS the card
     in place instead of scrolling to a separate card (client feedback). ── */
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => {
      setCarouselIndex(carouselApi.selectedScrollSnap());
      const entry = perSize[carouselApi.selectedScrollSnap()]?.entry;
      if (entry) onSizeChange?.(entry.size);
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselApi]);

  const cardCount = perSize.length;
  const clampedIndex = Math.min(carouselIndex, Math.max(cardCount - 1, 0));
  const progress = cardCount > 1 ? clampedIndex / (cardCount - 1) : 1;
  const canScrollPrev = clampedIndex > 0;
  const canScrollNext = clampedIndex < cardCount - 1;

  const stageToggle = (fullWidth = false) =>
    !isInstant ? (
      <div
        role="tablist"
        aria-label={copy.stage.challenge}
        className={cn(
          "flex items-center rounded-full p-[0.625rem]",
          fullWidth ? "w-full bg-[#f1f1f1]" : "w-fit bg-white",
        )}>
        {(["challenge", "funded"] as Stage[]).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={effectiveStage === s}
            onClick={() => onStageChange(s)}
            className={cn(
              "rounded-full px-[1.75rem] py-[1.375rem] font-geist text-[2rem] leading-[1.3] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]",
              fullWidth && "flex-1",
              effectiveStage === s ? "bg-[#0d2922] text-white" : "text-black hover:bg-black/5",
            )}>
            {s === "challenge" ? copy.stage.challenge : copy.stage.funded}
          </button>
        ))}
      </div>
    ) : (
      <span
        className={cn(
          "w-fit rounded-full bg-white px-[2.25rem] py-[1.375rem] font-geist text-[2.25rem] leading-[1.3] font-medium text-[#0d2922]",
          fullWidth && "w-full bg-[#f1f1f1] text-center",
        )}>
        {copy.stage.instantOnly}
      </span>
    );

  const labelCell = (row: Row) => (
    <span className="flex min-w-0 items-center gap-[0.75rem]">
      {row.tooltipKeys.length > 0 && (
      <InfoTip label={row.label}>
        <span className="flex flex-col gap-[0.75rem]">
          {row.tooltipKeys.map((k) => (
            <span
              key={k}
              className="block">
              <strong>{copy.kpiLabels[k]}:</strong> {copy.kpiTooltips[k]}
            </span>
          ))}
        </span>
      </InfoTip>
      )}
      <span className="font-geist text-[2.125rem] leading-[1.25] font-medium text-black/50">{row.label}</span>
    </span>
  );

  const ctaFor = (sub: SubCol, size: SizeId) => {
    const cta = makeCta(sub.presel);
    return cta ? (
      <CTAButton
        data={cta}
        overrideVariant="default"
        className="h-[6rem] w-full"
        onClick={() => onCheckout(sub.presel, sub.variant, size)}
      />
    ) : null;
  };

  return (
    <div className="relative w-full rounded-[1rem] bg-[#f1f1f1] p-[2rem]">
      {/* Desktop */}
      <div className="hidden gap-[2rem] lg:flex">
        {/* label column — its pre-row heights mirror the value-column box model
            exactly (wrapper pt-1 + card p-1 + header h-17.75 + card gap-2),
            so label rows and card rows align by construction in both modes. */}
        <div className="flex w-[34rem] shrink-0 flex-col pt-[2rem]">
          <div className="flex h-[17.75rem] flex-col items-start justify-between">
            {stageToggle()}
            {/* <div className="flex flex-col gap-[0.5rem]"> */}
            {/*   <span className="font-geist text-[2.5rem] leading-[1.3] font-medium text-black"> */}
            {/*     {copy.products[product.id].name} */}
            {/*   </span> */}
            {/*   <span className="font-geist text-[1.75rem] leading-[1.3] tracking-wide text-black/40 uppercase"> */}
            {/*     {copy.keyFeatures} */}
            {/*   </span> */}
            {/* </div> */}
          </div>
          {/* the card's gap between header and content */}
          <div className="h-[2rem]" />
          <div className="flex flex-col">
            {/* the "Chosen Path" row labels the variant sub-columns (Figma 6142:894) */}
            {anySplit && (
              <div className={cn(ROW_H, "flex items-center border-b border-dashed border-black/10")}>
                <span className="font-geist text-[2.125rem] leading-[1.25] font-medium text-black/50">
                  {copy.chosenPath}
                </span>
              </div>
            )}
            {rows.map((row) => (
              <div
                key={row.key}
                className={cn(ROW_H, "flex items-center border-b border-dashed border-black/10 last:border-b-0")}>
                {labelCell(row)}
              </div>
            ))}
          </div>
        </div>

        {/* size columns */}
        <div className="flex min-w-0 flex-1 items-stretch gap-[1.5rem] pt-[1rem]">
          {perSize.map(({ entry, subCols }) => {
            const highlighted = entry.size === highlightedSize;
            return (
              <div
                key={entry.size}
                className={cn(
                  "relative flex min-w-0 flex-1 flex-col gap-[2rem] rounded-[0.75rem] bg-white p-[1rem]",
                  highlighted && "border-2 border-[#307f6a] bg-[#f5f9f8]",
                )}>
                {highlighted && (
                  <span className="absolute -top-[4.5rem] left-[0.75rem] rounded-t-[0.375rem] bg-[#ff3f3f] px-[1.5rem] py-[1rem] font-geist text-[1.75rem] leading-[1.3] whitespace-nowrap text-white">
                    {copy.badgeMostPopular}
                  </span>
                )}

                {/* one shared price header per size — split cards render the
                    variant values as paired columns inside shared rows */}
                <DarkHeader
                  size={entry.size}
                  copy={copy}
                  highlighted={highlighted}>
                  <HeaderPrices
                    subCols={subCols}
                    copy={copy}
                    discounted={discounted}
                  />
                </DarkHeader>

                <div className="flex flex-1 flex-col px-[1.5rem]">
                  {anySplit && (
                    <div className={cn(ROW_H, "flex items-center gap-[1rem] border-b border-dashed border-black/10")}>
                      {subCols.map((sub) => (
                        <span
                          key={sub.presel + sub.variant}
                          className="min-w-0 flex-1 font-geist text-[2.125rem] leading-[1.25] font-medium text-black/50">
                          {variantLabel(sub.variant) || copy.products[product.id].name}
                        </span>
                      ))}
                    </div>
                  )}
                  {rows.map((row) => (
                    <div
                      key={row.key}
                      className={cn(
                        ROW_H,
                        "flex items-center gap-[1rem] border-b border-dashed border-black/10 last:border-b-0",
                      )}>
                      {subCols.length ? (
                        subCols.map((sub) => (
                          <span
                            key={sub.presel + sub.variant}
                            className="min-w-0 flex-1 font-geist text-[2.125rem] leading-[1.25] font-medium text-black/80">
                            {cellValue(sub, row)}
                          </span>
                        ))
                      ) : (
                        <span className="font-geist text-[2.125rem] leading-[1.25] font-medium text-black/80">
                          {copy.values.dash}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-auto flex gap-[1.5rem] pt-[0.5rem]">
                  {subCols.map((sub) => (
                    <div
                      key={sub.presel + sub.variant}
                      className="min-w-0 flex-1">
                      {ctaFor(sub, entry.size)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile — carousel of size × variant cards (Figma 6238:6219) */}
      <div className="flex flex-col gap-[2.5rem] lg:hidden">
        <Carousel
          setApi={setCarouselApi}
          opts={{ align: "start", loop: false }}
          className="w-full">
          <CarouselContent className="-ml-[1.5rem]">
            {perSize.map(({ entry, subCols }) => {
              const highlighted = entry.size === highlightedSize;
              return (
                <CarouselItem
                  key={entry.size}
                  className="basis-[88%] pl-[1.5rem] sm:basis-[70%]">
                  <div className="relative h-full pt-[3.25rem]">
                    {highlighted && (
                      <span className="absolute top-[0.25rem] left-[0.75rem] rounded-t-[0.375rem] bg-[#ff3f3f] px-[1.5rem] py-[0.25rem] font-geist text-[1.75rem] leading-[1.3] whitespace-nowrap text-white">
                        {copy.badgeMostPopular}
                      </span>
                    )}
                    <MobileSizeCard
                      entry={entry}
                      subCols={subCols}
                      rows={rows}
                      copy={copy}
                      discounted={discounted}
                      highlighted={highlighted}
                      stageToggle={stageToggle(true)}
                      labelCell={labelCell}
                      cellValue={cellValue}
                      ctaFor={ctaFor}
                      variantLabel={variantLabel}
                    />
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        {/* arrows + progress (Figma 6238:6326) */}
        {cardCount > 1 && (
          <div className="flex items-center gap-[1.5rem]">
            <button
              type="button"
              onClick={() => carouselApi?.scrollPrev()}
              aria-label="Previous"
              className={cn(
                "flex h-[5.25rem] w-[5.25rem] shrink-0 items-center justify-center rounded-full transition-all duration-200",
                canScrollPrev ? "bg-black" : "cursor-default bg-black/10 opacity-50 backdrop-blur-[0.177rem]",
              )}>
              <ArrowRightIcon
                className={cn("h-[2.125rem] w-[2.125rem] rotate-180", canScrollPrev ? "text-white" : "text-black")}
              />
            </button>
            <div className="h-[0.5rem] flex-1 overflow-hidden rounded-full bg-black/15">
              <div
                className="h-full rounded-full bg-black transition-[width] duration-300"
                style={{ width: `${Math.max(progress, 1 / Math.max(cardCount, 1)) * 100}%` }}
              />
            </div>
            <button
              type="button"
              onClick={() => carouselApi?.scrollNext()}
              aria-label="Next"
              className={cn(
                "flex h-[5.25rem] w-[5.25rem] shrink-0 items-center justify-center rounded-full transition-all duration-200",
                canScrollNext ? "bg-black" : "cursor-default bg-black/10 opacity-50 backdrop-blur-[0.177rem]",
              )}>
              <ArrowRightIcon
                className={cn("h-[2.125rem] w-[2.125rem]", canScrollNext ? "text-white" : "text-black")}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
